import styled from 'styled-components';
import tw, { css } from 'twin.macro';
import { Meta } from '../../../styled components/Typography';

/* The refresh control at the right edge of the toolbar: how fresh the shown
 * bookings are, and the split button that acts on it. Nothing in here changes
 * size between its states — a refresh in progress must not move the toolbar.
 *
 * The button borrows the segmented control's frame: the same border, radius
 * and segment padding, so the two controls at either end of the display band
 * are built out of the same shape. */

export const RefreshSplitButton = styled.div(() => [
  tw`inline-flex items-stretch rounded border border-solid border-silver-darken-10 bg-white`,
  /* 28px, the height of a small outlined button and of the segmented control
     at the other end of the band; the segments stretch to it, which is also
     what gives the divider between them its full height */
  `min-height: 1.75rem;`
]);

const refreshSegment = [
  tw`flex items-center justify-center m-0 p-0 border-0 cursor-pointer appearance-none bg-transparent`,
  tw`text-black text-opacity-60 transition-colors duration-100`,
  tw`can-hover:hover:(bg-silver-lighten-20 text-opacity-85) focus-visible:(bg-silver-lighten-20 text-opacity-85)`,
  tw`disabled:(cursor-not-allowed bg-transparent text-opacity-45)`,
  /* 3px 7px, the small outlined button's padding: the frame's border is on the
     control rather than on the segment, so both segments are exactly as high
     as the frame is */
  `padding: 0.1875rem 0.4375rem;`
];

/* The refresh glyph doubles as the spinner, so a refresh (started by hand or
   by the clock) turns the button that is already there rather than swapping in
   a loading indicator of another size */
export const RefreshNowSegment = styled.button<{ $spinning: boolean }>(
  ({ $spinning }) => [
    ...refreshSegment,
    tw`rounded-l text-xl`,
    $spinning &&
      css`
        & svg {
          ${tw`animate-spin`}
        }
      `
  ]
);

/* The chevron opening the interval menu, divided off by a hairline that runs
   the full height of the frame */
export const RefreshMenuSegment = styled.button(() => [
  ...refreshSegment,
  tw`rounded-r text-md`,
  tw`border-0 border-l border-solid border-silver-darken-10`
]);

/* When the bookings were last refreshed. It dims while the next refresh is on
   its way and updates in place when that one lands, so the line neither moves
   nor disappears */
export const RefreshTimestamp = styled(Meta)<{ $dimmed: boolean }>(({ $dimmed }) => [
  tw`whitespace-nowrap transition-opacity duration-100`,
  $dimmed && tw`opacity-45`
]);

/* The surface the refresh menu is drawn on. It borrows a card's white ground
   and hairline border, but it is a menu and not a card: it holds lines that
   are picked from, sizes itself to the control it hangs off, and carries none
   of a card's padding around its content */
export const RefreshMenuSurface = styled.div(
  tw`bg-white border border-solid border-silver-lighten-20 rounded p-4`
);

/* One line of the refresh menu. The checkmark column is always there, so the
   labels line up whether or not their line is the selected one */
export const RefreshMenuItem = styled.button(
  tw`flex items-center w-full m-0 py-4 px-8 border-0 rounded bg-transparent cursor-pointer text-left can-hover:hover:bg-silver-lighten-20 focus-visible:bg-silver-lighten-20`
);

export const RefreshMenuItemMark = styled.span(
  tw`flex-none flex items-center justify-center w-20 mr-4`
);
